import { Box, Typography } from "@material-ui/core";
import {
	CaseEventBasicValue,
	CaseEventMultiPartValue,
	CaseEventValue,
} from "apps/platform-client/client/src/models/case-event-log/common-event-types";
import { Maybe } from "apps/platform-client/client/src/models/common/generic-types";
import { CaseEventType } from "apps/platform-client/client/src/models/generated/graphql";
import renderCurrency from "apps/platform-client/client/src/utils/currency";
import { localeDateTime } from "apps/platform-client/client/src/utils/locale-date-time";
import { Link } from "react-router-dom";
import { GetSanitizedCaseEventLogsQuery } from "../../../types/queries.generated";

const caseEventLogTranslationMap = {
	[CaseEventType.AttributesChange]: "case-event-type-attributes-change",
	[CaseEventType.CaseCreation]: "case-event-type-case-creation",
	[CaseEventType.NegotiableAttributesAddition]: "case-event-negotiable-attributes-addition",
	[CaseEventType.NegotiationRoundRejection]: "case-event-type-negotiation-round-rejection",
	[CaseEventType.ThreadCreation]: "case-event-type-thread-creation",
	[CaseEventType.ReplyAddition]: "case-event-type-reply-addition",
	[CaseEventType.UserMention]: "case-event-type-user-mention",
	[CaseEventType.FileAttachment]: "case-event-type-file-attachment",
	[CaseEventType.CaseAcceptance]: "case-event-type-case-acceptance",
	[CaseEventType.CaseStateChange]: "case-event-type-case-state-change",
	[CaseEventType.FormAddition]: "case-event-type-form-addition",
	[CaseEventType.AutoDiscontinuation]: "case-event-type-form-addition-autodiscontinuation",
	[CaseEventType.ManualAgreementExecution]: "case-event-type-manual-agreement-execution",
	[CaseEventType.CaseProcessedWithoutReview]: "case-event-type-case-processed-without-review",
	[CaseEventType.RecordRevocation]: "case-event-type-record-revocation",
	[CaseEventType.FormDeletion]: "case-event-type-form-deletion",
	[CaseEventType.FormEdit]: "case-event-type-form-edit",
	[CaseEventType.RecordAddition]: "case-event-type-record-matched",
	[CaseEventType.RecordUpdate]: "case-event-type-record-updated",
	[CaseEventType.FormAdditionQuestAutoGeneration]: "case-event-type-form-addition-quest-autogeneration",
};

export function getEventDescriptionString(
	eventLog: GetSanitizedCaseEventLogsQuery["GetSanitizedCaseEventLogs"][0],
	t: (_v: string) => string,
	userTenantId: string,
	currencyCode: Maybe<string>
): any {
	const translationKey = caseEventLogTranslationMap[eventLog.eventType];
	const translation = t(translationKey);

	if (eventLog.data) {
		const parseData = JSON.parse(eventLog.data);
		const dataKeys: Array<string> = Object.keys(parseData);

		const regex = new RegExp(dataKeys.map((dataKey) => `\\$\\{${dataKey}\\}`).join("|"), "g");
		const newlineParts = translation.split(/\\n/g);

		return (
			<Box style={{ whiteSpace: "pre-wrap" }}>
				{newlineParts.map((newlinePart) => {
					const splitTranslation = newlinePart.split(/\$\{[a-zA-Z]*\}/g);
					const templatePlaceholders = Array.from(newlinePart.matchAll(regex));

					return (
						<Box display="flex" flexDirection="row" flexWrap="wrap" fontSize={"14px"}>
							{templatePlaceholders.map((matched, index) => {
								const key = matched[0].replace(/\$|\{|\}/g, "");
								const data = parseData[key];

								return (
									<Box display="flex" flexDirection="row" flexWrap="wrap">
										<Typography variant="inherit">{splitTranslation[index]}</Typography>
										{parseRoot(data, t, userTenantId, currencyCode)}
									</Box>
								);
							})}
							<Typography variant="inherit">{splitTranslation[splitTranslation.length - 1]}</Typography>
						</Box>
					);
				})}
			</Box>
		);
	}

	return translation;
}

const parseRoot = (data: CaseEventValue, t: (key: string) => string, userTenantId: string, currencyCode: Maybe<string>): any => {
	if (data.isArray) {
		return parseArrayValue(data.value as Array<CaseEventMultiPartValue>, t, userTenantId, currencyCode);
	}

	if ((data.value as CaseEventMultiPartValue).isMultiPartValue) {
		return parseMultiPartValue(
			(data.value as CaseEventMultiPartValue).value as Array<CaseEventBasicValue>,
			t,
			userTenantId,
			currencyCode
		);
	}

	return parseBasicValue((data.value as CaseEventMultiPartValue).value as CaseEventBasicValue, t, userTenantId, currencyCode);
};

const parseArrayValue = (
	values: Array<CaseEventMultiPartValue>,
	t: (key: string) => string,
	userTenantId: string,
	currencyCode: Maybe<string>
): any => {
	return (
		<Box>
			{values.map((value) =>
				value.isMultiPartValue
					? parseMultiPartValue(value.value as Array<CaseEventBasicValue>, t, userTenantId, currencyCode)
					: parseBasicValue(value.value as CaseEventBasicValue, t, userTenantId, currencyCode)
			)}
		</Box>
	);
};

const parseMultiPartValue = (
	values: Array<CaseEventBasicValue>,
	t: (key: string) => string,
	userTenantId: string,
	currencyCode: Maybe<string>
): any => {
	return (
		<Box fontSize={"14px"} display="flex" flexDirection="row" flexWrap="wrap">
			{...values.map((value) => parseBasicValue(value, t, userTenantId, currencyCode))}
		</Box>
	);
};

const parseBasicValue = (
	value: CaseEventBasicValue,
	t: (key: string) => string,
	userTenantId: string,
	currencyCode: Maybe<string>
): any => {
	if (value.value === undefined || value.value === null) {
		return <></>;
	}

	return value.isHyperlink && (!value.creatorTenantId || value.creatorTenantId === userTenantId) ? (
		<Link to={value.hyperlink} target="_blank">
			<Typography variant="inherit" style={{ ...value.style }}>
				{getFormattedValue(value, t, currencyCode)}
			</Typography>
		</Link>
	) : (
		<Typography variant="inherit" style={{ ...value.style }}>
			{"\u00a0" + getFormattedValue(value, t, currencyCode)}
		</Typography>
	);
};

const getFormattedValue = (value: CaseEventBasicValue, t: (key: string) => string, currencyCode: Maybe<string>) => {
	if (value.isTranslatable) {
		return t(value.value);
	}

	if (value.isDateTimeValue) {
		return localeDateTime(new Date(value.value));
	}

	if (value.isFinancialValue) {
		const maskedValue = "XX";
		return value.value === maskedValue ? value.value : renderCurrency(value.value, currencyCode);
	}

	return value.value;
};
